<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Ativo' : 'Adicionar Ativo' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
        <span v-else>Salvar</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label for="invDescricao" class="form-label">Descrição</label>
          <input id="invDescricao" v-model="form.descricao" type="text" required placeholder="Ex: Ações Petrobras" class="form-input" />
        </div>

        <div>
          <label for="invTipo" class="form-label">Tipo de Investimento</label>
          <select id="invTipo" v-model="form.tipo" required class="form-select">
            <option disabled value="">Selecione um tipo</option>
            <option v-for="t in tiposInvestimento" :key="t.value" :value="t.value">{{ t.label }}</option>
          </select>
        </div>

        <div v-if="showTickerInput">
          <label for="invTicker" class="form-label">Ticker</label>
          <input id="invTicker" v-model="form.ticker" type="text" placeholder="Ex: PETR4" class="form-input uppercase" />
        </div>

        <div>
          <label for="invInstituicao" class="form-label">Instituição / Corretora</label>
          <input id="invInstituicao" v-model="form.instituicao" type="text" placeholder="Ex: XP, Nubank" class="form-input" />
        </div>

        <div>
          <label for="invValor" class="form-label">Valor Atual (R$)</label>
          <input id="invValor" v-model.number="form.valorAtual" type="number" step="0.01" required placeholder="0.00" class="form-input" />
        </div>
      </form>
    </div>

    <p v-if="loading" class="text-sm text-slate-500 dark:text-slate-400">Carregando...</p>
    <p v-if="error" class="text-sm text-rose-600 dark:text-rose-400 font-semibold">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const route = useRoute()

const isEditing = computed(() => !!route.params.ativoId)
const isSubmitting = ref(false)
const loading = ref(false)
const error = ref('')

const API = 'https://backend.controlease.com.br/api'
const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}
const ensureAuth = () => {
  const token = localStorage.getItem('token')
  if (!token) {
    alert('Sessão expirada. Faça login novamente.')
    router.replace({ name: 'Login' })
    return false
  }
  return true
}

const tiposInvestimento = [
  { value: 'RENDA_FIXA', label: 'Renda Fixa' },
  { value: 'RENDA_VARIAVEL', label: 'Renda Variável (Ações, BDRs)' },
  { value: 'FUNDO_IMOBILIARIO', label: 'Fundo Imobiliário (FII)' },
  { value: 'TESOURO_DIRETO', label: 'Tesouro Direto' },
  { value: 'CRIPTOATIVO', label: 'Criptoativo' },
]

const getInitial = () => ({
  id: null,
  descricao: '',
  tipo: 'RENDA_VARIAVEL',
  instituicao: '',
  ticker: '',
  valorAtual: null,
  portfolioUuid: route.params.portfolioUuid || null
})

const form = ref(getInitial())

const showTickerInput = computed(() => {
  const t = form.value?.tipo
  return t === 'RENDA_VARIAVEL' || t === 'FUNDO_IMOBILIARIO'
})
watch(form, v => { if (v && !showTickerInput.value) v.ticker = '' }, { deep: true })

const hydrate = (data) => {
  form.value = {
    id: data?.id ?? null,
    descricao: data?.descricao ?? '',
    tipo: data?.tipo ?? 'RENDA_VARIAVEL',
    instituicao: data?.instituicao ?? '',
    ticker: data?.ticker ?? '',
    valorAtual: Number(data?.valorAtual ?? 0),
    portfolioUuid: data?.portfolio?.uuid ?? form.value.portfolioUuid ?? route.params.portfolioUuid ?? null
  }
}

const loadFromNavState = () => {
  const navItem = router.options.history?.state?.item || window.history.state?.item
  if (navItem && String(navItem.id ?? '') === String(route.params.ativoId ?? '')) {
    hydrate(navItem)
    return true
  }
  return false
}

const fetchAtivo = async () => {
  if (!ensureAuth()) return
  loading.value = true
  error.value = ''
  try {
    const { data } = await axios.get(`${API}/ativos-compartilhados/${route.params.ativoId}`, authConfig())
    hydrate(data)
  } catch (e) {
    const s = e.response?.status
    if (s === 401 || s === 403) {
      alert('Sessão expirada ou sem permissão. Faça login novamente.')
      router.replace({ name: 'Login' })
    } else {
      error.value = e.response?.data?.message || 'Erro ao carregar dados do ativo.'
    }
  } finally {
    loading.value = false
  }
}

const navigateToPortfolio = (portfolioUuid) => {
  if (portfolioUuid) {
    router.push({ name: 'InvestimentosCompartilhados', params: { id: String(portfolioUuid) } })
  } else {
    router.push({ name: 'InvestimentosCompartilhados' })
  }
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  if (!ensureAuth()) return
  isSubmitting.value = true
  try {
    const payload = {
      ...form.value,
      ticker: (form.value.ticker || '').toUpperCase(),
      valorAtual: Number(form.value.valorAtual)
    }
    let portfolioUuid = payload.portfolioUuid || route.params.portfolioUuid || null

    if (isEditing.value) {
      await axios.put(`${API}/ativos-compartilhados/${payload.id}`, payload, authConfig())
      portfolioUuid = portfolioUuid || payload.portfolioUuid
    } else {
      if (!portfolioUuid) throw new Error('UUID do portfólio não encontrado.')
      await axios.post(`${API}/investimentos-compartilhados/${portfolioUuid}/ativos`, payload, authConfig())
    }

    navigateToPortfolio(portfolioUuid)
  } catch (error) {
    const s = error.response?.status
    if (s === 401 || s === 403) {
      alert('Sessão expirada ou sem permissão. Faça login novamente.')
      router.replace({ name: 'Login' })
    } else {
      alert(error.response?.data?.message || 'Erro ao salvar ativo.')
    }
  } finally {
    isSubmitting.value = false
  }
}

const goBack = () => {
  const portfolioUuid = form.value.portfolioUuid || route.params.portfolioUuid || null
  navigateToPortfolio(portfolioUuid)
}

onMounted(() => {
  if (isEditing.value) {
    if (!loadFromNavState()) fetchAtivo()
  } else {
    form.value = getInitial()
    ensureAuth()
  }
})
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-brand-800/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.form-input, .form-select { @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500; }
.form-select { @apply appearance-none; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
